'use client';
import { AXES, type Axis, type AxisState, type GuessFeedback, type Specs } from '../lib/types';

// Headers carry the unit so the value cell only has to fit the number — the
// weight column ("3,200") is the widest, and pulling "lb" up here is what lets
// the 8-wide grid fit a phone in portrait.
const HEADERS: Record<Axis, string> = {
  era: 'Era', origin: 'Origin', layout: 'Layout', body_style: 'Body',
  cylinders: 'Cyl', aspiration: 'Aspir', power: 'Power', weight: 'Wt·lb',
};

const ARROW: Partial<Record<AxisState, string>> = {
  higher: '▲',
  lower: '▼',
  close_higher: '▲',
  close_lower: '▼',
};

function cellBg(state: AxisState): string {
  // Heat scale: green = right, amber = close, red = off. The ▲/▼ arrow (its own
  // slot below the value) carries direction for BOTH close (amber) and far (red)
  // numeric cells; categorical-wrong is the only arrow-less red.
  if (state === 'exact' || state === 'match') return '#3a9d5d'; // green
  if (state === 'close_higher' || state === 'close_lower') return '#d8a72b'; // amber
  return '#b3433a'; // red — far (numeric, with arrow) or wrong (categorical)
}

function cellValue(axis: Axis, specs: Specs): string {
  if (axis === 'cylinders' && specs.engine_config === 'rotary') return 'Rotary';
  return String({
    era: specs.year, origin: specs.origin, layout: specs.layout,
    body_style: specs.body_style, cylinders: specs.cylinders,
    aspiration: specs.aspiration, power: specs.power_hp,
    // US audience: curb weight shown in pounds (canon stores kg). Unit lives in
    // the header now, so the value is just the number.
    weight: Math.round(specs.weight_kg * 2.20462).toLocaleString('en-US'),
  }[axis]);
}

export function DecodeBoard({
  rows,
}: {
  rows: { label: string; specs: Specs; feedback: GuessFeedback }[];
}) {
  return (
    <div className="decode-board">
      {/* Ghost rubric row — before the first guess, show WHAT the eight
          graded specs are so the rubric costs zero guesses to learn. Replaced
          by real rows the moment the first guess lands. */}
      {rows.length === 0 && (
        <div className="decode-row decode-ghost" aria-hidden>
          <div className="decode-row-label">Your first guess lands here</div>
          <div className="decode-grid">
            {AXES.map((axis) => (
              <div key={axis} className="decode-cell">
                <span className="decode-cell-head">{HEADERS[axis]}</span>
                <span className="decode-cell-val">—</span>
                <span className="decode-cell-arrow"> </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {rows.map((row, ri) => (
        <div key={ri} className="decode-row">
          <div className="decode-row-label">{row.label}</div>
          <div className="decode-grid">
            {AXES.map((axis) => {
              const state = row.feedback[axis];
              return (
                <div
                  key={axis}
                  title={HEADERS[axis]}
                  className="decode-cell"
                  style={{ background: cellBg(state) }}
                >
                  <span className="decode-cell-head">{HEADERS[axis]}</span>
                  <span className="decode-cell-val">{cellValue(axis, row.specs)}</span>
                  {/* Arrow always renders its slot (blank when no direction) so
                      every cell in a row stays the same height. */}
                  <span className="decode-cell-arrow">{ARROW[state] ?? ' '}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
